import api from '@/api/api.ts';
import { useApiGet } from '@/api/apiRequests.ts';
import { getOrps } from '@/api/services';
import { getMetrics } from '@/api/services/getMetrics.ts';
import { PageLoader } from '@/components/PageLoader.tsx';
import { AxiosError } from 'axios';
import { Button } from 'primereact/button';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { FC, useState } from 'react';
import { toast } from 'react-toastify';

export const Bubble: FC = () => {
  const [year, setYear] = useState<number>(2023);
  const [selectedOrp, setSelectedOrp] = useState<string>('Brno');
  const [selectedInputs, setSelectedInputs] = useState<string>('nehody_i');
  const [svgData, setSvgData] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const { data: orpsData } = useApiGet<any, AxiosError>(['orps'], async () => await getOrps(), {
    enabled: true,
  });

  const { data: metricsData } = useApiGet<any, AxiosError>(['metrics'], async () => await getMetrics(), {
    enabled: true,
  });

  const generateBubbleChart = async () => {
    setIsLoading(true);
    try {
      const response = await api.get(
        `/plot/svg?metric_code=${selectedInputs}&year=${year}&orp=${selectedOrp}`,
        {
          responseType: 'text',
        }
      );

      if (response.status === 200 || response.status === 201 || response.status === 202) {
        setSvgData(response.data);
        toast.success('Bubble chart has been successfully generated.');
      }
    } catch (error: any) {
      if (error?.response?.data?.detail) {
        toast.error(`Error: ${error.response.data.detail}`);
      } else {
        toast.error('Error generating bubble chart. Please try again.');
      }
      console.error('Error fetching SVG:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerate = () => {
    generateBubbleChart();
  };

  const downloadBubblesZip = async () => {
    setIsDownloading(true);
    try {
      const response = await api.get(
        `/bubbles-to-zip?year=${year}&orp=${selectedOrp}`,
        {
          responseType: 'blob',
        }
      );

      if (response.status === 200 || response.status === 201 || response.status === 202) {
        // Create blob link to download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${selectedOrp}_bubbles_${year}.zip`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        toast.success('File downloaded successfully.');
      }
    } catch (error: any) {
      if (error?.response?.data?.detail) {
        toast.error(`Error: ${error.response.data.detail}`);
      } else {
        toast.error('Error downloading file. Please try again.');
      }
      console.error('Error downloading file:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <h1 className={'text-2xl font-bold mb-8 text-white'}>Bubble Chart</h1>

      <div className={'flex flex-col justify-center items-center gap-6 max-w-2xl mx-auto'}>
        <div className="field w-full">
          <label htmlFor="year" className="font-bold text-white mb-2 block">
            Year
          </label>
          <InputNumber
            id="year"
            value={year}
            onValueChange={(e) => setYear(e.value || 2023)}
            min={2000}
            max={2030}
            className="w-full"
          />
        </div>

        <div className="field w-full">
          <label htmlFor="orp" className="font-bold text-white mb-2 block">
            ORP
          </label>
          <Dropdown
            id="orp"
            value={selectedOrp}
            onChange={(e: DropdownChangeEvent) => setSelectedOrp(e.value)}
            options={orpsData ? orpsData.map((item: any) => ({ 
              label: item.municipality?.name || item.municipality?.orp_name || '', 
              value: item.municipality?.name || item.municipality?.orp_name || '' 
            })) : []}
            optionLabel="label"
            optionValue="value"
            filter={true}
            placeholder="Choose ORP"
            className="w-full"
          />
        </div>

        <div className="field w-full">
          <label htmlFor="inputs" className="font-bold text-white mb-2 block">
            Inputs
          </label>
          <Dropdown
            id="inputs"
            value={selectedInputs}
            onChange={(e: DropdownChangeEvent) => setSelectedInputs(e.value)}
            options={
              metricsData
                ? metricsData
                    .filter((item: any) =>
                      item.metric_code.endsWith('_i') ||
                      ['ikz_prace', 'ikz_vztahy', 'ikz_zdravi'].includes(item.metric_code)
                    )
                    .map((item: any) => ({ label: item.metric_code, value: item.metric_code }))
                : []
            }
            optionLabel="label"
            optionValue="value"
            filter={true}
            placeholder="Choose inputs"
            className="w-full"
          />
        </div>

        <div className="flex gap-4 w-full">
          <Button
            className={'btn-primary px-8 py-3 flex-1'}
            style={{ borderRadius: '6px' }}
            onClick={handleGenerate}
            disabled={isLoading || isDownloading}
          >
            {isLoading ? 'Generating...' : 'Show This Bubble Chart'}
          </Button>

          <Button
            className={'btn-secondary px-8 py-3 flex-1'}
            style={{ borderRadius: '6px' }}
            onClick={downloadBubblesZip}
            disabled={isLoading || isDownloading}
          >
            {`Download All ${selectedOrp} Bubbles for ${year}`}
          </Button>
        </div>
      </div>

      {svgData && (
        <div className="mt-8 flex justify-center w-full">
          <div
            className="border border-gray-300 rounded-lg p-4 bg-white w-full max-w-6xl mx-auto overflow-hidden"
            style={{ minHeight: '400px' }}
          >
            <div
              className="w-full h-full flex items-center justify-center"
              style={{ minHeight: '350px' }}
            >
              <div
                className="w-full h-full flex items-center justify-center"
                dangerouslySetInnerHTML={{ __html: svgData }}
                style={{
                  width: '100%',
                  height: '100%',
                  minHeight: '300px'
                }}
              />
            </div>
          </div>
        </div>
      )}

      <PageLoader
        visible={isLoading || isDownloading}
        text={isDownloading ? 'Bubbles are generating...' : 'Generating bubble chart...'}
      />
    </>
  );
};
